import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { Table, Statistic } from 'antd';
import uniqBy from 'lodash/uniqBy';
import classNames from 'classnames';

import { pageChanged } from '../../../../../data/redux/page_details/actions';
import * as usageActions from '../../../../../data/redux/usage_details/actions';

import { APP_PAGES } from '../../../../../data/config/constants';
import { BRAND_INFO } from '../../../../../data/config/urls';

import CustomHeader from '../../../../../components/customheader';
import ChartStatusCard from '../../../components/chartstatuscard';
import Graph from '../../../components/charts/graph';
import CardContainer from '../../../components/cardcontainer';

const session_granularity_options = [
  { value: 'hour', name: 'Hour' },
  { value: 'day', name: 'Day' },
  { value: 'week', name: 'Week' },
  { value: 'month', name: 'Month' },
];

const retention_granularity_options = [
  { value: 'day', name: 'Day' },
  { value: 'week', name: 'Week' },
  { value: 'month', name: 'Month' },
];

const ConversationStatisticsTabItem = props => {
  const { title, value, active } = props;
  const fontSize = parseInt(value) <= 9999 ? '3.5rem' : (parseInt(value) <= 99999 ? '3rem' : '2.5rem');
  return (
    <div className={classNames("ori-pad-10 ori-flex-row ori-bg-hover-default ori-r-border-solid ori-border-width-3", { "ori-border-color-green ori-bg-default": active, "ori-border-color-transparent": !active })}>
      <div className="ori-flex-column ori-flex-jc ori-overflow-hidden ori-lr-pad-10" style={{ width: '40%' }}>
        <Statistic valueStyle={{ fontWeight: 'bold', fontSize: fontSize }} value={value} />
      </div>
      <div className="ori-flex-column ori-flex-jc ori-l-pad-10 ori-loader-line-height" style={{ width: '60%' }}>
        <span className="ori-text-overflow-dotted">{title}</span>
      </div>
    </div>
  );
};

class UsageAnalytics extends Component {
  componentWillMount() {
    const { actions } = this.props;
    const current_module = BRAND_INFO.usage_home_page ? APP_PAGES.DASHBOARD : APP_PAGES.ANALYTICS;
    const current_page = BRAND_INFO.usage_home_page ? APP_PAGES.DASHBOARD : APP_PAGES.USAGE_ANALYTICS;
    actions.pageChanged(current_page, current_module);
  }

  componentDidMount() {
    const { actions, usage_details } = this.props;
    if (usage_details.chat_session_time_series.length === 0) {
      const payload = {
        startDate: usage_details.filters.date_range[0],
        endDate: usage_details.filters.date_range[1],
        platform: usage_details.filters.platform,
        granularity: usage_details.chat_session_filters.granularity
      };
      actions.getChatSessionTimeSeries(payload);
      actions.getUserEngagementStats(payload);
    }
    if (usage_details.user_retention.cohorts_data.length === 0 || usage_details.user_retention.cohorts_columns.length === 0) {
      const payload = {
        startDate: usage_details.filters.date_range[0],
        endDate: usage_details.filters.date_range[1],
        platform: usage_details.filters.platform,
        granularity: usage_details.user_retention.retention_filters.granularity,
      };
      actions.getUserRetention(payload);
    }
  }

  updateChatSessionTickInterval = (data, granularity) => {
    const { usage_details, actions } = this.props;
    const uniqPlatforms = uniqBy(data, 'platformType');
    let offset = parseInt(data.length / (uniqPlatforms.length * 10));
    const hour = 60 * 60 * 1000;
    offset = offset >= 1 ? offset : 1;
    const timestamp = {
      ...usage_details.chat_session_scale.timestamp,
      tickInterval: granularity === "day" ? hour * 24 * offset : (granularity === "week" ? hour * 24 * 7 * offset : (granularity === "month" ? hour * 24 * 15 * offset : hour * offset)),
      mask: granularity === "hour" ? 'DD MMM HH:mm' : 'DD MMM',
    };
    actions.updateUsageState(timestamp, 'chat_session_scale.timestamp');
  };

  onChangeDateRange = date_range => {
    const { actions, usage_details } = this.props;
    let session_granularity = usage_details.chat_session_filters.granularity;
    let retention_granularity = usage_details.user_retention.retention_filters.granularity;
    const payload = {
      startDate: date_range[0],
      endDate: date_range[1],
      platform: usage_details.filters.platform,
    };
    const num_of_days = date_range[1].diff(date_range[0], 'days') + 1;
    if (num_of_days === 1) {
      session_granularity = 'hour';
      retention_granularity = 'day';
    } else if (num_of_days <= 7) {
      session_granularity = 'day';
      retention_granularity = 'day';
    } else if (num_of_days <= 60) {
      session_granularity = 'week';
      retention_granularity = 'week';
    } else {
      session_granularity = 'month';
      retention_granularity = 'month';
    }
    actions.getUserEngagementStats({ ...payload, granularity: session_granularity });
    actions.getChatSessionTimeSeries({ ...payload, granularity: session_granularity }, data => {
      this.updateChatSessionTickInterval(data, session_granularity);
      actions.updateUsageState(session_granularity, "chat_session_filters.granularity");
    });
    actions.getUserRetention({ ...payload, granularity: retention_granularity }, () => actions.updateUsageState(retention_granularity, "user_retention.retention_filters.granularity"));
    actions.updateUsageState(date_range, "filters.date_range");
  };

  onChangeGranularity = event => {
    const { actions, usage_details } = this.props;
    const platform = event.target.value;
    const payload = {
      startDate: usage_details.filters.date_range[0],
      endDate: usage_details.filters.date_range[1],
      platform,
    };
    actions.getChatSessionTimeSeries({ ...payload, granularity: usage_details.chat_session_filters.granularity });
    actions.getUserEngagementStats({ ...payload, granularity: usage_details.chat_session_filters.granularity });
    actions.getUserRetention({ ...payload, granularity: usage_details.user_retention.retention_filters.granularity });
    actions.updateUsageState(platform, "filters.platform");
  };

  onChangeChatSessionDropdown = granularity => {
    const { actions, usage_details } = this.props;
    const payload = {
      startDate: usage_details.filters.date_range[0],
      endDate: usage_details.filters.date_range[1],
      platform: usage_details.filters.platform,
      granularity,
    };
    actions.getUserEngagementStats(payload);
    actions.getChatSessionTimeSeries(payload, data => {
      this.updateChatSessionTickInterval(data, granularity);
      actions.updateUsageState(granularity, "chat_session_filters.granularity");
    });
  };

  onChangeRetentionDropdown = granularity => {
    const { actions, usage_details } = this.props;
    const payload = {
      startDate: usage_details.filters.date_range[0],
      endDate: usage_details.filters.date_range[1],
      platform: usage_details.filters.platform,
      granularity,
    };
    actions.getUserRetention(payload, () => actions.updateUsageState(granularity, "user_retention.retention_filters.granularity"));
  };

  renderUserEngagementStatsCard = () => {
    const { user_engagement_stats, loaders } = this.props.usage_details;
    return (
      <CardContainer card_container_class="ori-animated ori-fade-in" loader_container_class="ori-opacity-medium" container_height={265} emtpy_condition={user_engagement_stats.length === 0} load_error={loaders.user_stats_load_error} loading={(user_engagement_stats.length === 0 || loaders.user_stats_load_error) ? loaders.user_stats_loading : false} >
        <div className="ori-flex ori-flex-wrap ori-t-pad-10 ori-lr-mrgn-neg-10">
          {
            user_engagement_stats.map((item, index) => {
              return (
                <div key={index} className="ori-stats-card-width ori-pad-10">
                  <ChartStatusCard card_data={item} loading={loaders.user_stats_loading} value_key="count" tooltip_visible />
                </div>
              );
            })
          }
        </div>
      </CardContainer>
    );
  };

  renderConversationStatisticsCard = () => {
    const { need_attentions, live_chats, overtaken_chats } = this.props.usage_details.conversation_log_stats;
    const custom_header_props = {
      title: "Conversation Log",
      container_class: "ori-lr-pad-15"
    };
    return (
      <CardContainer custom_header_props={custom_header_props} container_height={350} card_container_class="ori-animated ori-fade-in ori-box-shadow-light ori-tb-mrgn-5 ori-bg-white ori-b-pad-10" show_custom_header>
        <div className="ori-tb-pad-15 ori-flex-column ori-flex-jc" style={{ height: '350px' }}>
          <ConversationStatisticsTabItem title="Need Attentions" value={need_attentions} />
          <ConversationStatisticsTabItem title="Live Chats" value={live_chats} />
          <ConversationStatisticsTabItem title="Overtaken Chats" value={overtaken_chats} />
        </div>
      </CardContainer>
    );
  };

  renderChatSessionTimeSeries = () => {
    const { chat_session_time_series, chat_session_scale, chat_session_filters, loaders } = this.props.usage_details;
    const custom_header_props = {
      title: "Chat Sessions",
      dropdown_options: session_granularity_options,
      dropdown_value: chat_session_filters.granularity,
      onChangeDropdown: this.onChangeChatSessionDropdown,
      show_dropdown: true,
    };
    return (
      <CardContainer custom_header_props={custom_header_props} emtpy_condition={chat_session_time_series.length === 0} load_error={loaders.chat_session_load_error} loading={loaders.chat_session_loading} show_custom_header>
        <Graph type="area" data={chat_session_time_series} x_axis="timestamp" y_axis="count" color_key="platformType" scale={chat_session_scale} shape="smooth" height={340} padding={[60, 25, 50, 60]} legend />
      </CardContainer>
    );
  };

  renderRetentionTable = () => {
    const { screen_width } = this.props;
    const { user_retention, loaders } = this.props.usage_details;
    const custom_header_props = {
      title: "User Retention",
      dropdown_options: retention_granularity_options,
      dropdown_value: user_retention.retention_filters.granularity,
      onChangeDropdown: this.onChangeRetentionDropdown,
      show_dropdown: true,
    };
    return (
      <CardContainer custom_header_props={custom_header_props} container_height={450} emtpy_condition={user_retention.cohorts_data.length === 0} load_error={loaders.user_retention_load_error} loading={loaders.user_retention_loading} show_custom_header>
        <Table className="ori-table-default ori-table-content-align-center ori-table-header-bg-transparent" size={screen_width <= 576 ? "small" : "middle"} scroll={{ x: 600 }} columns={user_retention.cohorts_columns} dataSource={user_retention.cohorts_data} />
      </CardContainer>
    );
  };

  render() {
    const { usage_details, allowedChannels } = this.props;
    const { chat_session_loading, user_retention_loading, user_stats_loading } = this.props.usage_details.loaders;
    const channels = [{ value: 'all', name: 'All' }, ...allowedChannels];
    return (
      <div className="ori-lr-pad-10 oriUsageAnalyticsContainer">
        <CustomHeader title={BRAND_INFO.usage_home_page ? "Usages" : "Usages Analytics"} granularity_options={channels} granularity_value={usage_details.filters.platform} granularity_disabled={chat_session_loading || user_retention_loading || user_stats_loading} onChangeGranularity={this.onChangeGranularity} date_range={usage_details.filters.date_range} date_range_disabled={chat_session_loading || user_retention_loading || user_stats_loading} onChangeDateRange={this.onChangeDateRange} bottom_bordered show_date_range show_granularity />
        {this.renderUserEngagementStatsCard()}
        {/* <Row gutter={10}>
          <Col xs={24} sm={7}>
            {this.renderConversationStatisticsCard()}
          </Col>
          <Col xs={24} sm={17}> */}
        {this.renderChatSessionTimeSeries()}
        {/* </Col>
        </Row> */}
        {this.renderRetentionTable()}
      </div>
    );
  }
}

const mapStateToProps = state => {
  return {
    usage_details: state.usage_details,
    is_chats_socket_connected: state.chats_details.is_socket_connected,
    screen_width: state.page_details.device_data.screen_width ? state.page_details.device_data.screen_width : window.innerWidth,
    allowedChannels: state.admin_details.allowedChannels,
  };
};

const mapDispatchToProps = dispatch => {
  return {
    actions: bindActionCreators(Object.assign({}, { pageChanged }, usageActions), dispatch),
  };
};

ConversationStatisticsTabItem.propTypes = {
  title: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  active: PropTypes.bool,
};

ConversationStatisticsTabItem.defaultProps = {
  active: false,
};

UsageAnalytics.propTypes = {
  actions: PropTypes.object,
  screen_width: PropTypes.number,
  allowedChannels: PropTypes.array,
  usage_details: PropTypes.object,
  history: PropTypes.object,
  match: PropTypes.object,
};

export default connect(mapStateToProps, mapDispatchToProps, null, { withRef: true })(UsageAnalytics);



// WEBPACK FOOTER //
// ./src/app/pagecontainer/modules/analytics/usageanalytics/index.js